import React, { useState } from 'react';
import {
  Activity,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  Info,
  PenTool,
  QrCode,
  RotateCw,
  Search,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MAX_SCALE, MIN_SCALE, PDFViewerState } from '../../types';

const MAX_DISPLAY_SCALE = 5;

interface PDFToolbarProps {
  viewerState: PDFViewerState;
  fileName: string;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRotate: () => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onClose: () => void;
  onDownload: () => void;
  onResetView: () => void;
}

export const PDFToolbar: React.FC<PDFToolbarProps> = ({
  viewerState,
  fileName,
  onZoomIn,
  onZoomOut,
  onRotate,
  onPreviousPage,
  onNextPage,
  onClose,
  onDownload,
  onResetView,
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const { currentPage, numPages, scale } = viewerState;
  const maxAllowedScale = Math.min(MAX_SCALE, MAX_DISPLAY_SCALE);
  const zoomLabel = scale >= 10 ? `${scale.toFixed(1)}x` : `${Math.round(scale * 100)}%`;

  return (
    <div className="relative border-b border-border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-2 px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <Button variant="outline" size="sm" className="max-w-[260px] justify-between">
            <span className="truncate text-sm">{fileName}</span>
            <ChevronDown className="ml-2 h-4 w-4 text-muted-foreground" />
          </Button>
          <Button variant="outline" size="sm" className="justify-between">
            <span className="text-sm">Rev. 0</span>
            <ChevronDown className="ml-2 h-4 w-4 text-muted-foreground" />
          </Button>
        </div>

        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={onPreviousPage} disabled={currentPage <= 1}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center space-x-2 px-3 py-1 bg-muted rounded-md">
            <span className="text-sm font-medium text-foreground">{currentPage}</span>
            <span className="text-sm text-muted-foreground">/</span>
            <span className="text-sm text-muted-foreground">{numPages || 1}</span>
          </div>
          <Button variant="outline" size="sm" onClick={onNextPage} disabled={numPages <= 0 || currentPage >= numPages}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex flex-1 items-center justify-end space-x-2">
          <Button variant="outline" size="sm" onClick={onZoomOut} disabled={scale <= MIN_SCALE}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <div className="px-3 py-1 bg-muted rounded-md min-w-[90px] text-center">
            <span className="text-sm font-medium text-foreground">{zoomLabel}</span>
          </div>
          <Button variant="outline" size="sm" onClick={onZoomIn} disabled={scale >= maxAllowedScale}>
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onResetView}>
            Reset
          </Button>
          <Button variant="outline" size="sm" onClick={onRotate}>
            <RotateCw className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onDownload}>
            <Download className="mr-1 h-4 w-4" />
            Download
          </Button>
          <Button variant="ghost" size="sm" className="text-muted-foreground">
            <QrCode className="mr-1 h-4 w-4" />
            QR Code
          </Button>
          <Button variant="ghost" size="sm" className="text-muted-foreground">
            <PenTool className="mr-1 h-4 w-4" />
            Markup
          </Button>
          <Button variant="ghost" size="sm" className="text-muted-foreground">
            <Filter className="mr-1 h-4 w-4" />
            Filter
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHelp((prev) => !prev)}
            className={showHelp ? 'bg-accent text-foreground' : 'text-muted-foreground'}
          >
            <Info className="mr-1 h-4 w-4" />
            Info
          </Button>
          <Button variant="ghost" size="sm" className="text-muted-foreground">
            <Search className="mr-1 h-4 w-4" />
            Search
          </Button>
          <Button variant="ghost" size="sm" className="text-muted-foreground">
            <Activity className="mr-1 h-4 w-4" />
            Activity
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="mr-1 h-4 w-4" />
            Close
          </Button>
        </div>
      </div>

      {showHelp && (
        <div className="absolute right-4 top-full mt-2 bg-popover text-popover-foreground border border-border text-xs rounded-lg p-3 shadow-lg z-50 w-72">
          <div className="space-y-1">
            <div className="font-semibold mb-2">Controls</div>
            <div>Scroll / trackpad: zoom at cursor</div>
            <div>iPad two-finger pinch: zoom at finger midpoint</div>
            <div>Left-click drag: pan</div>
            <div>Toolbar +/-: centered zoom</div>
            <div className="mt-2 pt-2 border-t border-border text-muted-foreground">
              Zoom range: {MIN_SCALE * 100}% - {maxAllowedScale * 100}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
